import React, { useEffect, useRef, useCallback, useState } from "react";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { isEmpty, isEqual } from "lodash";
import classNames from "classnames";

import { useStateSelector } from "utils/field";
import { getOptions, getFirstOption, getDefaultOption } from "utils/fields";
import { required, maxLength, getValidation } from "utils/fieldValidation";
import { standardCodeLogic } from "utils/codes";
import { getModelDataComponentPath } from "utils/enclosure/codeDrawerMeasurements";
import { getSelectedComponents } from "utils/parametric";
import crawlspaceWallCodeTemplate from "features/Model/Enclosure/Templates/wallCode.json";

import { actions as userActions } from "store/users";
import { actions as enclosureActions } from "features/Model/Enclosure/_ducks";
import { actions as upgradeActions } from "features/Model/Upgrades/_ducks";
import { removeFormErrors } from "store/form/actions";

import InputRow from "components/Input/Row";
import Input from "components/Input";
import Select from "components/Input/Select";
import Checkbox from "components/Input/Checkbox";
import Field from "components/Field";
import Button from "components/Button";
import Tooltip from "components/Tooltip";

import PercentGradeSwitch from "components/EmbodiedCarbonGrade/PercentGradeSwitch";
import EmbodiedCarbonFramingGroupGrade, {
    EmbodiedCarbonCalculationsSummary,
    StandardEmbodiedLayerBlock,
} from "components/EmbodiedCarbonGrade";
import globalStyles from "components/globalStyles.module.scss";
import CodeLibraryDestination from "components/CodeLibraryDestination";

const { addToCodeLib, deleteCode } = userActions;
const { setInitCode, getRValue } = enclosureActions;
const { updateUpgradeCodes } = upgradeActions;

const charLim32 = maxLength(32);

//Note that this uses the exact same fields as a standard wall code selector
const labelValidation = [required, charLim32];
const interiorValidation = getValidation("csWallInterior");
const structureValidation = getValidation("csWallStructureType");
const typeSizeValidation = getValidation("csWallComponentTypeSize");
const sheathingValidation = getValidation("csWallSheathing");
const framingSpacingValidation = getValidation("csWallFramingSpacing");
const exteriorValidation = getValidation("csWallExterior");
const ins1Validation = getValidation("csWallInsLayer1");
const ins2Validation = getValidation("csWallInsLayer2");
const studsValidation = getValidation("csWallStudsCornersInt");

const StandardCrawlspaceCode = React.memo(
    ({
        toggleOpen,
        componentId,
        fieldAccessor,
        isCodeLibrary = false,
        change,
        upgradeWhereToAddCode = {},
        submitting, // Not passed in as prop
        invalid, // Not passed in as prop
        clearInitCode,
        handleSubmit,
        removeFormErrors,
        CodeTypeSelect,
    }) => {
        const [barMetric, setBarMetric] = useState("percentage");

        const drawerAccessor = `newForm.codeDrawer.fields`;

        const setDrawerState = (value) => {
            change(`${drawerAccessor}`, value);
        };

        const codeFields = useStateSelector(`${drawerAccessor}`) || {};
        const selectedComponentsForUpgrade = useStateSelector(`newForm.upgradeDrawer.selectedComponents`) || [];

        const uid = useStateSelector(`user.uid`);
        const { crawlspaceWallInsType = {} } = useStateSelector(`enclosure.initCodeValues`) || {};
        const { currentPackage, selectedUpgrade } = useStateSelector(`upgradeLibrary`) || {};
        const { modelId, modelData: { codes: modelCodes = {}, components = {} } = {} } =
            useStateSelector(`model`) || {};
        const { components: componentsFromParametric = {} } =
            useStateSelector(`parametricAnalysis.parametricAnalysis.model.modelData`) || {};

        const crawlspaceWallCode = useStateSelector(`${drawerAccessor}.crawlspaceWallCode`) || {};
        const { id: crawlspaceWallStructureId, value: crawlspaceWallStructureValue = 0 } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.structureType`) || {};
        const { id: crawlspaceWallSpacingId, value: crawlspaceWallSpacingValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.spacing`) || {};
        const { value: crawlspaceWallTypeSizeValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.componentTypeSize`) || {};
        const { value: crawlspaceWallFramingValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.framing`) || {};
        const { value: crawlspaceWallIns1Value } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer1`) || {};
        const { value: crawlspaceWallIns2Value } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer2`) || {};
        const { value: crawlspaceWallInteriorValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.interior`) || {};
        const { value: crawlspaceWallSheathingValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.sheathing`) || {};
        const { value: crawlspaceWallExteriorValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.exterior`) || {};
        const { value: crawlspaceWallStudsValue } =
            useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers.studsCornerIntersection`) || {};
        const crawlspaceWallLabel = useStateSelector(`${drawerAccessor}.crawlspaceWallCode.label`) || "";
        const crawlspaceWallLayers = useStateSelector(`${drawerAccessor}.crawlspaceWallCode.layers`) || {};
        const layersFieldPrefix = `${drawerAccessor}.crawlspaceWallCode.layers`;

        const frameSpacingValue =
            crawlspaceWallSpacingId === null ? crawlspaceWallFramingValue : crawlspaceWallSpacingValue;
        const crawlspaceWallValue = `1${crawlspaceWallStructureValue
            .toString()
            .substr(
                -1
            )}${crawlspaceWallTypeSizeValue}${frameSpacingValue}${crawlspaceWallIns1Value}${crawlspaceWallIns2Value}${crawlspaceWallInteriorValue}${crawlspaceWallSheathingValue}${crawlspaceWallExteriorValue}${
            crawlspaceWallStudsValue || 0
        }`;

        const isSolid = parseFloat(crawlspaceWallStructureId) === 4;
        const isPanel = parseFloat(crawlspaceWallStructureId) === 5;
        const isFloorHeader = parseFloat(crawlspaceWallStructureId) === 6;

        const makeSpacingDefault = ["5", "6", "7", "8", "9", "A", "B", "C"].includes(crawlspaceWallTypeSizeValue);
        const makeInsulationDefault = ["6", "7", "8", "9", "A", "B", "C"].includes(crawlspaceWallTypeSizeValue);

        const { codeRef: editingCodeRef = null, isLibCode = false } = crawlspaceWallInsType;
        const isEditing = !!editingCodeRef;
        const isUpgradeComponent = fieldAccessor.includes("fields.");

        const componentPath = isUpgradeComponent ? "" : getModelDataComponentPath("CrawlspaceWall", fieldAccessor);
        const selectedComponents = isUpgradeComponent ? selectedComponentsForUpgrade : [componentPath];

        const { upgradeSubType, upgradeType } = upgradeWhereToAddCode?.ecmInputMapUpgrade || {};

        const embcModelComponents = modelId ? components : componentsFromParametric;
        const embcSelectedComponents = isEqual(selectedComponents, ["*"])
            ? getSelectedComponents(upgradeSubType, upgradeType, embcModelComponents)
            : selectedComponents;

        const valueRef = useRef(null);

        const handleStuctureChange = useCallback(
            ({ id, value }) => {
                change(
                    `${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer1`,
                    getFirstOption({ indKey: id, depKey: "csWallInsLayer1" })
                );
                change(
                    `${drawerAccessor}.crawlspaceWallCode.layers.componentTypeSize`,
                    getFirstOption({ indKey: id, depKey: "csWallComponentTypeSize" })
                );

                // Use "Framing" when set to Solid, use Spacing otherwise
                if (parseFloat(id) === 4) {
                    // Set spacing to null and grab first option for framing
                    change(`${drawerAccessor}.crawlspaceWallCode.layers.spacing`, { id: null });
                    change(
                        `${drawerAccessor}.crawlspaceWallCode.layers.framing`,
                        getFirstOption({
                            indKey: id,
                            depKey: "csWallFramingSpacing",
                        })
                    );
                } else {
                    // Set framing to null and grab first option for spacing
                    change(`${drawerAccessor}.crawlspaceWallCode.layers.framing`, { id: null });

                    // Only grab default spacing id if not set
                    if (crawlspaceWallSpacingId === null) {
                        change(
                            `${drawerAccessor}.crawlspaceWallCode.layers.spacing`,
                            getDefaultOption("csWallFramingSpacing")
                        );
                    }
                }

                // Panel
                if (parseFloat(id) === 5) {
                    change(
                        `${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer1`,
                        getDefaultOption("csWallInsLayer1")
                    );
                    change(
                        `${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer2`,
                        getDefaultOption("csWallInsLayer2")
                    );
                    change(
                        `${drawerAccessor}.crawlspaceWallCode.layers.spacing`,
                        getDefaultOption("csWallFramingSpacing")
                    );
                    change(
                        `${drawerAccessor}.crawlspaceWallCode.layers.studsCornerIntersection`,
                        getDefaultOption("csWallStudsCornersInt")
                    );
                }
            },
            [crawlspaceWallStructureId, change, crawlspaceWallSpacingId]
        );

        useEffect(() => {
            const initialValues = {
                crawlspaceWallCode: isEmpty(crawlspaceWallInsType) ? crawlspaceWallCodeTemplate : crawlspaceWallInsType,
                addToLibrary: isLibCode,
            };

            setDrawerState(initialValues);
        }, []);

        useEffect(() => {
            valueRef.current = crawlspaceWallValue;
        }, [crawlspaceWallValue, crawlspaceWallLabel]);

        // Removes floor header from opts
        const excludeValues = ["8"];
        const crawlWallStructureOpts = getOptions({ fieldKey: "csWallStructureType" }).filter(
            (opt) => !excludeValues.includes(opt.value.value)
        );

        // Prevents crashing when field values are null
        if (isEmpty(codeFields)) {
            return <React.Fragment></React.Fragment>;
        }

        const crawlspaceEmbRowStyle = {
            border: "1px solid #E0E0E0",
            borderRadius: "4px",
            marginBottom: "4px",
        };

        return (
            <div className={globalStyles.standardCodeWrapper}>
                {/* <h3>Standard Code</h3> */}
                <InputRow gridTemplate={CodeTypeSelect ? "1fr 1fr 1fr" : "1fr 1fr"} style={{ alignItems: "flex-end" }}>
                    {CodeTypeSelect && <CodeTypeSelect />}
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.crawlspaceWallCode.label`}
                        label="Code Label"
                        placeholder="Code Label"
                        validate={labelValidation}
                        setValue={crawlspaceWallLabel.toString() === valueRef.current ? crawlspaceWallValue : null}
                    />
                    <Field
                        component={Input}
                        type="text"
                        name={`${drawerAccessor}.crawlspaceWallCode.value`}
                        label="Internal Code"
                        placeholder="###########"
                        disabled={true}
                        setValue={crawlspaceWallValue}
                    />
                    {!isCodeLibrary && (
                        <>
                            <div data-tip="Code will automatically be updated in library" data-for="codeLibTip">
                                <CodeLibraryDestination
                                    drawerAccessor={drawerAccessor}
                                    editingCodeRef={editingCodeRef}
                                    isLibCode={isLibCode}
                                />
                            </div>
                            <Field
                                component={Checkbox}
                                name={`${drawerAccessor}.setAsModelDefault`}
                                label={"Set as model default"}
                                large
                                type="checkbox"
                                style={{ whiteSpace: "nowrap" }}
                            />
                        </>
                    )}
                </InputRow>

                <div className={globalStyles.assemblyLayersHeading}>
                    <h4>Assembly Layers</h4>
                    <PercentGradeSwitch activeTab={barMetric} setActiveTab={setBarMetric} />
                </div>

                <div className={globalStyles.framingGroup}>
                    <InputRow
                        style={{
                            gridGap: "0",
                            border: "1px solid #e1eaf0",
                            borderBottom: "none",
                            borderRadius: "4px 4px 0px 0px",
                        }}
                        lastRow={true}
                    >
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.crawlspaceWallCode.layers.structureType`}
                            label="Structure Type"
                            placeholder="Choose Structure Type"
                            options={crawlWallStructureOpts}
                            validate={structureValidation}
                            onChange={handleStuctureChange}
                            isFlatDesign={true}
                            borderBottom={true}
                            customBorderRadius={{ borderRadius: "4px 4px 0px 0px" }}
                        />
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.crawlspaceWallCode.layers.componentTypeSize`}
                            label="Component Type / Size"
                            placeholder="Choose Type / Size"
                            options={getOptions({
                                fieldKey: "csWallComponentTypeSize",
                                indKey: crawlspaceWallStructureId,
                            })}
                            validate={typeSizeValidation}
                            disabled={isFloorHeader}
                            onChange={({ value = "" }) => {
                                if (["6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(
                                        `${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer1`,
                                        getDefaultOption("csWallInsLayer1")
                                    );
                                    change(
                                        `${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer2`,
                                        getDefaultOption("csWallInsLayer2")
                                    );
                                }
                                if (["5", "6", "7", "8", "9", "A", "B", "C"].includes(value)) {
                                    change(`${drawerAccessor}.crawlspaceWallCode.layers.framing`, {
                                        id: 0,
                                        value: "0",
                                    });
                                }
                            }}
                            isFlatDesign={true}
                            borderBottom={true}
                        />
                    </InputRow>
                    <InputRow
                        style={{
                            gridGap: "0",
                            borderBottom: "1px solid #e1eaf0",
                            borderLeft: "1px solid #e1eaf0",
                            borderRight: "1px solid #e1eaf0",
                            borderTop: "none",
                            borderRadius: "0px 0px 4px 4px",
                        }}
                        lastRow={true}
                    >
                        {isSolid ? (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.crawlspaceWallCode.layers.framing`}
                                label="Framing"
                                placeholder="Choose Framing"
                                options={getOptions({
                                    fieldKey: "csWallFramingSpacing",
                                    indKey: crawlspaceWallStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={makeSpacingDefault}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        ) : (
                            <Field
                                component={Select}
                                name={`${drawerAccessor}.crawlspaceWallCode.layers.spacing`}
                                label="Spacing"
                                placeholder="Choose Spacing"
                                options={getOptions({
                                    fieldKey: "csWallFramingSpacing",
                                    indKey: crawlspaceWallStructureId,
                                })}
                                validate={framingSpacingValidation}
                                disabled={isPanel || isFloorHeader}
                                isFlatDesign={true}
                                borderBottom={true}
                            />
                        )}
                        <Field
                            component={Select}
                            name={`${drawerAccessor}.crawlspaceWallCode.layers.studsCornerIntersection`}
                            label="Studs at Corners and Intersections"
                            placeholder="Choose"
                            options={getOptions({ fieldKey: "csWallStudsCornersInt" })}
                            validate={studsValidation}
                            disabled={isSolid || isPanel || isFloorHeader}
                            isFlatDesign={true}
                            customBorderRadius={{ borderRadius: "0px 0px 4px 4px" }}
                        />
                    </InputRow>
                    <EmbodiedCarbonFramingGroupGrade
                        layers={crawlspaceWallLayers}
                        componentType="crawlspaceWall"
                        customPadding="4px 0 0 0"
                        change={change}
                        layersFieldPrefix={layersFieldPrefix}
                        customGap={"1rem"}
                        barMetric={barMetric}
                    />
                </div>

                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer1"
                    layers={crawlspaceWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={crawlspaceEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer1`}
                        label="Framing Cavity Insulation (Layer 1)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csWallInsLayer1", indKey: crawlspaceWallStructureId })}
                        validate={ins1Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="insulationLayer2"
                    layers={crawlspaceWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    editorDisabled={isPanel || makeInsulationDefault}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={crawlspaceEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.crawlspaceWallCode.layers.insulationLayer2`}
                        label="Continuous Layer of Insulation (Layer 2)"
                        placeholder="Choose Insulation"
                        options={getOptions({ fieldKey: "csWallInsLayer2" })}
                        validate={ins2Validation}
                        disabled={isPanel || makeInsulationDefault}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <StandardEmbodiedLayerBlock
                    layerType="interior"
                    layers={crawlspaceWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    editorDisabled={isFloorHeader}
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={crawlspaceEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.crawlspaceWallCode.layers.interior`}
                        label="Interior"
                        placeholder="Choose Interior"
                        options={getOptions({ fieldKey: "csWallInterior" })}
                        validate={interiorValidation}
                        disabled={isFloorHeader}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="sheathing"
                    layers={crawlspaceWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={crawlspaceEmbRowStyle}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.crawlspaceWallCode.layers.sheathing`}
                        label="Sheathing"
                        placeholder="Choose Sheathing"
                        options={getOptions({ fieldKey: "csWallSheathing" })}
                        validate={sheathingValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>
                <StandardEmbodiedLayerBlock
                    layerType="exterior"
                    layers={crawlspaceWallLayers}
                    change={change}
                    layersFieldPrefix={layersFieldPrefix}
                    componentType="crawlspaceWall"
                    withBorder={true}
                    borderHeight="36px"
                    customGap="0.5rem"
                    rowStyle={{ ...crawlspaceEmbRowStyle, marginBottom: 0 }}
                    barMetric={barMetric}
                >
                    <Field
                        component={Select}
                        name={`${drawerAccessor}.crawlspaceWallCode.layers.exterior`}
                        label="Exterior"
                        placeholder="Choose Exterior"
                        options={getOptions({ fieldKey: "csWallExterior" })}
                        validate={exteriorValidation}
                        isFlatDesign={true}
                        customBorderRadius={{ borderRadius: "4px 0px 0px 4px" }}
                        withRightBorder={true}
                    />
                </StandardEmbodiedLayerBlock>

                <EmbodiedCarbonCalculationsSummary
                    componentId={componentId}
                    code={crawlspaceWallCode}
                    componentType={"crawlspaceWall"}
                    modelDataComponents={embcModelComponents}
                    selectedComponents={embcSelectedComponents}
                />

                <div style={{ borderBottom: "1px solid #e1eaf0", marginTop: "1.5rem", marginBottom: "0.5rem" }}></div>

                <div className={classNames(globalStyles.buttons, globalStyles.fixedDrawerButtons)}>
                    <Button
                        large
                        type="hollow"
                        onClick={() => {
                            clearInitCode();
                            setDrawerState({});
                            toggleOpen(false);
                            removeFormErrors(`${drawerAccessor}.crawlspaceWallCode.label`);
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        large
                        onClick={() => {
                            handleSubmit({
                                change,
                                codeFields,
                                toggleOpen,
                                uid,
                                fieldAccessor,
                                editingCodeRef,
                                isCodeLibrary,
                                componentId,
                                isEditing,
                                modelCodes,
                                components,
                                currentPackage,
                                selectedUpgrade,
                                upgradeWhereToAddCode,
                            });
                            // Clear wall code defaults for next time
                            clearInitCode();
                            // Reset state
                            setDrawerState({});
                        }}
                        disabled={submitting || invalid}
                    >
                        {submitting ? "Saving Code..." : "Save Code"}
                    </Button>
                </div>
                <Tooltip id="codeLibTip" hide={!isLibCode} />
            </div>
        );
    }
);

const mapStateToProps = createStructuredSelector({});

const mapDispatchToProps = (dispatch) => ({
    clearInitCode: () => dispatch(setInitCode({}, "crawlspaceWallInsType")),
    handleSubmit: ({
        change,
        codeFields,
        toggleOpen,
        uid,
        fieldAccessor,
        editingCodeRef,
        isCodeLibrary,
        componentId,
        isEditing,
        modelCodes,
        components,
        currentPackage,
        selectedUpgrade,
        upgradeWhereToAddCode = {},
    }) => {
        const {
            crawlspaceWallCode = {},
            addToLibrary = false,
            orgLibraryId = "",
            setAsModelDefault = false,
            selectedComponents = [],
        } = codeFields;

        toggleOpen(false);

        const {
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            ecmInputMapLocation = "parametricAnalysis",
        } = upgradeWhereToAddCode || {};

        // Run standard code logic
        standardCodeLogic({
            change,
            componentCodeType: "CrawlspaceWall",
            componentId,
            code: crawlspaceWallCode,
            inputName: "crawlspaceWallInsType",
            codeType: "S",
            isCodeLibrary,
            isEditing,
            editingCodeRef,
            dispatch,
            getRValue,
            fieldAccessor,
            components,
            modelCodes,
            addToLibrary,
            orgLibraryId,
            addToCodeLib,
            uid,
            deleteCode,
            updateUpgradeCodes,
            setAsModelDefault,
            selectedComponents,
            currentPackage,
            selectedUpgrade,
            ecmInputMapUpgrade,
            ecmInputMapUpgradeId,
            ecmInputMapUpgradeType,
            isEcmInputMap: !isEmpty(upgradeWhereToAddCode),
            ecmInputMapLocation,
        });
    },
    removeFormErrors: (errorName) => dispatch(removeFormErrors(errorName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(StandardCrawlspaceCode);
